import { Component, createRef } from "react";

// ─────────────────────────────────────────────
// SHARED STYLES
// ─────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;1,400&family=Playfair+Display:wght@700&display=swap');`;

const sharedStyles = `
  ${FONTS}

  .ai-root {
    font-family: 'DM Sans', sans-serif;
    background: transparent;
    width: 100%;
    box-sizing: border-box;
  }
  .ai-root * { box-sizing: border-box; }

  /* ── Upload Zone ── */
  .ai-upload-zone {
    border: 2px dashed #c9c4bb;
    border-radius: 12px;
    padding: 48px 24px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    background: #faf9f7;
    margin-bottom: 24px;
  }
  .ai-upload-zone:hover, .ai-upload-zone.drag-over {
    border-color: #1a1a1a;
    background: #f0ede8;
  }
  .ai-upload-label {
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    color: #1a1a1a;
    margin-bottom: 6px;
  }
  .ai-upload-sub { font-size: 13px; color: #888; }

  /* ── Canvas ── */
  .ai-canvas-wrapper {
    position: relative;
    width: 100%;
  }
  .ai-svg {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    overflow: visible;
  }
  .ai-image {
    display: block;
    width: 100%;
    border-radius: 10px;
    user-select: none;
  }

  /* ── Dots ── */
  .ai-dot { cursor: pointer; pointer-events: all; }

  /* ── Annotation cards ── */
  .ai-annotations-layer {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    pointer-events: none;
  }
  .ai-annotation-card {
    position: absolute;
    pointer-events: all;
    background: #fff;
    border: 1.5px solid #e8e3dc;
    border-radius: 10px;
    padding: 12px 14px;
    width: 220px;
    box-shadow: 0 4px 18px rgba(0,0,0,0.08);
    transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s;
  }
  .ai-annotation-card.clickable { cursor: pointer; }
  .ai-annotation-card.clickable:hover,
  .ai-annotation-card.active {
    box-shadow: 0 8px 28px rgba(0,0,0,0.14);
    border-color: #1a1a1a;
    transform: translateY(-1px);
  }
  .ai-annotation-card h4 {
    font-family: 'Playfair Display', serif;
    font-size: 14px;
    margin: 0 0 5px 0;
    color: #1a1a1a;
    line-height: 1.3;
  }
  .ai-annotation-card p {
    font-size: 12px;
    color: #666;
    margin: 0;
    line-height: 1.5;
  }
  .ai-annotation-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px; height: 20px;
    border-radius: 50%;
    background: #1a1a1a;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  /* ── Buttons ── */
  .ai-btn {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 7px;
    border: 1.5px solid #1a1a1a;
    background: #fff;
    color: #1a1a1a;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .ai-btn:hover, .ai-btn.primary { background: #1a1a1a; color: #fff; }
  .ai-btn.generate {
    background: #1a1a1a;
    color: #fff;
    border-color: #1a1a1a;
    font-size: 14px;
    padding: 10px 22px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }
  .ai-btn.generate:hover { background: #333; }
  .ai-btn.danger { border-color: #e05050; color: #e05050; }
  .ai-btn.danger:hover { background: #e05050; color: #fff; }

  /* ── Controls bar ── */
  .ai-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
    align-items: center;
  }

  /* ── Editor panel ── */
  .ai-editor {
    background: #faf9f7;
    border: 1.5px solid #e8e3dc;
    border-radius: 10px;
    padding: 20px;
    margin-top: 20px;
  }
  .ai-editor h3 {
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    margin: 0 0 16px 0;
    color: #1a1a1a;
  }
  .ai-editor-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
    align-items: flex-end;
  }
  .ai-field { display: flex; flex-direction: column; gap: 4px; }
  .ai-field label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #888;
  }
  .ai-field input, .ai-field textarea, .ai-field select {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    padding: 8px 10px;
    border: 1.5px solid #e8e3dc;
    border-radius: 7px;
    background: #fff;
    color: #1a1a1a;
    outline: none;
    transition: border-color 0.15s;
  }
  .ai-field input:focus, .ai-field textarea:focus, .ai-field select:focus {
    border-color: #1a1a1a;
  }
  .ai-field textarea { resize: vertical; min-height: 70px; width: 100%; }

  /* ── Generated code block ── */
  .ai-codebox {
    margin-top: 24px;
    border: 1.5px solid #e8e3dc;
    border-radius: 10px;
    overflow: hidden;
  }
  .ai-codebox-header {
    background: #1a1a1a;
    color: #fff;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .ai-codebox-header span { opacity: 0.6; font-weight: 400; font-size: 12px; }
  .ai-codebox pre {
    margin: 0;
    padding: 18px;
    background: #f8f7f5;
    font-size: 11.5px;
    line-height: 1.6;
    overflow-x: auto;
    color: #2d2d2d;
    max-height: 380px;
    overflow-y: auto;
    font-family: 'Fira Code', 'Cascadia Code', 'Courier New', monospace;
  }
  .ai-copy-btn {
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.3);
    color: #fff;
    font-size: 12px;
    padding: 4px 12px;
    border-radius: 5px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: background 0.15s;
  }
  .ai-copy-btn:hover { background: rgba(255,255,255,0.25); }

  .ai-instructions {
    font-size: 12px;
    color: #aaa;
    margin-top: 8px;
    font-style: italic;
  }

  /* ── Divider ── */
  .ai-divider {
    border: none;
    border-top: 1.5px solid #e8e3dc;
    margin: 28px 0;
  }

  /* ── Generate section ── */
  .ai-generate-section {
    background: linear-gradient(135deg, #f8f7f5 0%, #f0ede8 100%);
    border: 1.5px solid #e8e3dc;
    border-radius: 12px;
    padding: 24px;
    margin-top: 24px;
  }
  .ai-generate-section h3 {
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    margin: 0 0 6px 0;
    color: #1a1a1a;
  }
  .ai-generate-section p {
    font-size: 13px;
    color: #777;
    margin: 0 0 18px 0;
    line-height: 1.5;
  }

  /* ── Viewer mode badge ── */
  .ai-viewer-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #e8f5e9;
    border: 1px solid #a5d6a7;
    color: #2e7d32;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    margin-left: 10px;
  }

  @media (max-width: 640px) {
    .ai-annotation-card { width: 160px; }
    .ai-annotation-card h4 { font-size: 12px; }
    .ai-annotation-card p { font-size: 11px; }
  }
`;

// ─────────────────────────────────────────────
// SHARED: AnnotationDisplay (used in both editor and viewer)
// ─────────────────────────────────────────────
class AnnotationDisplay extends Component {
  constructor(props) {
    super(props);
    this.imageRef = createRef();
    this.wrapperRef = createRef();
    this.resizeObserver = null;
    this.state = {
      renderedWidth: 0,
      renderedHeight: 0,
      activeId: null,
    };
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver(() => this.updateSize());
    if (this.wrapperRef.current)
      this.resizeObserver.observe(this.wrapperRef.current);
  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  updateSize = () => {
    const img = this.imageRef.current;
    if (!img) return;
    this.setState({
      renderedWidth: img.clientWidth,
      renderedHeight: img.clientHeight,
    });
  };

  handleImageLoad = () => this.updateSize();

  getCardStyle(ann) {
    const { renderedWidth, renderedHeight } = this.state;
    if (!renderedWidth || !renderedHeight) return { display: "none" };
    const cardH = 120;
    const cardW = window.innerWidth < 640 ? 160 : 220;
    const margin = 18;
    let top = (ann.pointY / 100) * renderedHeight - cardH / 2;
    top = Math.max(4, Math.min(top, renderedHeight - cardH - 4));
    const left =
      ann.side === "right" ? renderedWidth + margin : -cardW - margin;
    return { left: `${left}px`, top: `${top}px` };
  }

  getLinePoints(ann) {
    const { renderedWidth, renderedHeight } = this.state;
    if (!renderedWidth || !renderedHeight) return null;
    const cardH = 120;
    const margin = 18;
    const dotX = (ann.pointX / 100) * renderedWidth;
    const dotY = (ann.pointY / 100) * renderedHeight;
    let cardTop = dotY - cardH / 2;
    cardTop = Math.max(4, Math.min(cardTop, renderedHeight - cardH - 4));
    const cardMidY = cardTop + cardH / 2;
    const lineX2 = ann.side === "right" ? renderedWidth + margin : -margin;
    return { x1: dotX, y1: dotY, x2: lineX2, y2: cardMidY };
  }

  render() {
    const {
      imageSrc,
      annotations,
      editable,
      onEdit,
      onDelete,
      pendingPoint,
      mode,
    } = this.props;
    const { renderedWidth, renderedHeight, activeId } = this.state;

    const rightCards = annotations.filter((a) => a.side === "right");
    const leftCards = annotations.filter((a) => a.side === "left");
    const cardW = 220;
    const margin = 18;

    return (
      <div
        style={{
          paddingRight: rightCards.length > 0 ? cardW + margin + 16 : 0,
          paddingLeft: leftCards.length > 0 ? cardW + margin + 16 : 0,
          position: "relative",
        }}
      >
        <div
          ref={this.wrapperRef}
          className="ai-canvas-wrapper"
          style={{
            cursor:
              mode === "placing" && !pendingPoint ? "crosshair" : "default",
          }}
          onClick={this.props.onImageClick}
        >
          <img
            ref={this.imageRef}
            src={imageSrc}
            alt="Annotated"
            className="ai-image"
            onLoad={this.handleImageLoad}
            draggable={false}
          />

          {/* SVG lines + dots */}
          <svg
            className="ai-svg"
            style={{ overflow: "visible" }}
            viewBox={`0 0 ${renderedWidth} ${renderedHeight}`}
            preserveAspectRatio="none"
          >
            {annotations.map((ann) => {
              const pts = this.getLinePoints(ann);
              if (!pts) return null;
              return (
                <line
                  key={ann.id}
                  x1={pts.x1}
                  y1={pts.y1}
                  x2={pts.x2}
                  y2={pts.y2}
                  stroke={activeId === ann.id ? "#1a1a1a" : "#c0bbaf"}
                  strokeWidth={activeId === ann.id ? 2 : 1.5}
                  strokeDasharray="5,3"
                />
              );
            })}

            {annotations.map((ann, i) => {
              const cx = (ann.pointX / 100) * renderedWidth;
              const cy = (ann.pointY / 100) * renderedHeight;
              const isActive = activeId === ann.id;
              return (
                <g
                  key={ann.id}
                  className="ai-dot"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState((s) => ({
                      activeId: s.activeId === ann.id ? null : ann.id,
                    }));
                  }}
                >
                  {/* Halo: expands via strokeWidth, not transform — number stays put */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={13}
                    fill="none"
                    stroke="#1a1a1a"
                    strokeWidth={isActive ? 2.5 : 0}
                    opacity={0.15}
                    style={{ transition: "stroke-width 0.15s" }}
                  />
                  <circle
                    cx={cx}
                    cy={cy}
                    r={8}
                    fill={isActive ? "#1a1a1a" : "#fff"}
                    stroke="#1a1a1a"
                    strokeWidth={2}
                    style={{ transition: "fill 0.15s" }}
                  />
                  <text
                    x={cx}
                    y={cy + 4}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={600}
                    fontFamily="DM Sans, sans-serif"
                    fill={isActive ? "#fff" : "#1a1a1a"}
                    style={{
                      pointerEvents: "none",
                      userSelect: "none",
                      transition: "fill 0.15s",
                    }}
                  >
                    {i + 1}
                  </text>
                </g>
              );
            })}

            {pendingPoint && (
              <circle
                cx={(pendingPoint.x / 100) * renderedWidth}
                cy={(pendingPoint.y / 100) * renderedHeight}
                r={8}
                fill="#1a1a1a"
                opacity={0.4}
              />
            )}
          </svg>

          {/* Annotation cards */}
          <div className="ai-annotations-layer">
            {annotations.map((ann, i) => (
              <div
                key={ann.id}
                className={`ai-annotation-card${editable ? " clickable" : ""}${activeId === ann.id ? " active" : ""}`}
                style={this.getCardStyle(ann)}
                onClick={(e) => {
                  e.stopPropagation();
                  this.setState((s) => ({
                    activeId: s.activeId === ann.id ? null : ann.id,
                  }));
                }}
              >
                <div className="ai-annotation-number">{i + 1}</div>
                <h4>{ann.label}</h4>
                <p>{ann.description}</p>
                {editable && (
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button
                      className="ai-btn"
                      style={{ fontSize: 11, padding: "4px 10px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(ann);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="ai-btn danger"
                      style={{ fontSize: 11, padding: "4px 10px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(ann.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

// ─────────────────────────────────────────────
// CODE GENERATOR
// ─────────────────────────────────────────────
function generateViewerCode(imageSrc, annotations) {
  const annJson = JSON.stringify(annotations, null, 2)
    .split("\n")
    .map((l, i) => (i === 0 ? l : "  " + l))
    .join("\n");

  return `import { Component, createRef } from "react";

const ANNOTATIONS = ${annJson};

const IMAGE_SRC = "${imageSrc.length > 200 ? imageSrc.slice(0, 60) + "...[base64 truncated — paste your full base64 or use a URL]" : imageSrc}";

const STYLES = \`
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Playfair+Display:wght@700&display=swap');
.aiv-root { font-family: 'DM Sans', sans-serif; width: 100%; box-sizing: border-box; }
.aiv-root * { box-sizing: border-box; }
.aiv-canvas { position: relative; width: 100%; }
.aiv-svg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; overflow: visible; }
.aiv-img { display: block; width: 100%; border-radius: 10px; user-select: none; }
.aiv-dot { cursor: pointer; pointer-events: all; }
.aiv-layer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; }
.aiv-card {
  position: absolute; pointer-events: all;
  background: #fff; border: 1.5px solid #e8e3dc; border-radius: 10px;
  padding: 12px 14px; width: 220px;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08);
  cursor: pointer; transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s;
}
.aiv-card:hover, .aiv-card.active { box-shadow: 0 8px 28px rgba(0,0,0,0.14); border-color: #1a1a1a; transform: translateY(-1px); }
.aiv-card h4 { font-family: 'Playfair Display', serif; font-size: 14px; margin: 0 0 5px 0; color: #1a1a1a; line-height: 1.3; }
.aiv-card p { font-size: 12px; color: #666; margin: 0; line-height: 1.5; }
.aiv-num { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; border-radius: 50%; background: #1a1a1a; color: #fff; font-size: 11px; font-weight: 600; margin-bottom: 6px; }
@media (max-width: 640px) { .aiv-card { width: 160px; } .aiv-card h4 { font-size: 12px; } .aiv-card p { font-size: 11px; } }
\`;

class AnnotatedImageViewer extends Component {
  constructor(props) {
    super(props);
    this.imageRef = createRef();
    this.wrapperRef = createRef();
    this.resizeObserver = null;
    this.state = { w: 0, h: 0, activeId: null };
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver(() => this.updateSize());
    if (this.wrapperRef.current) this.resizeObserver.observe(this.wrapperRef.current);
  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  updateSize = () => {
    const img = this.imageRef.current;
    if (img) this.setState({ w: img.clientWidth, h: img.clientHeight });
  };

  cardStyle(ann) {
    const { w, h } = this.state;
    if (!w || !h) return { display: "none" };
    const cardH = 120, margin = 18;
    const cardW = window.innerWidth < 640 ? 160 : 220;
    let top = (ann.pointY / 100) * h - cardH / 2;
    top = Math.max(4, Math.min(top, h - cardH - 4));
    const left = ann.side === "right" ? w + margin : -cardW - margin;
    return { left, top };
  }

  linePoints(ann) {
    const { w, h } = this.state;
    if (!w || !h) return null;
    const cardH = 120, margin = 18;
    const dotX = (ann.pointX / 100) * w;
    const dotY = (ann.pointY / 100) * h;
    let cardTop = dotY - cardH / 2;
    cardTop = Math.max(4, Math.min(cardTop, h - cardH - 4));
    return { x1: dotX, y1: dotY, x2: ann.side === "right" ? w + margin : -margin, y2: cardTop + cardH / 2 };
  }

  render() {
    const { w, h, activeId } = this.state;
    const annotations = this.props.annotations || ANNOTATIONS;
    const imageSrc = this.props.imageSrc || IMAGE_SRC;
    const right = annotations.filter(a => a.side === "right").length > 0;
    const left = annotations.filter(a => a.side === "left").length > 0;

    return (
      <div className="aiv-root">
        <style>{\`\${STYLES}\`}</style>
        <div style={{ paddingRight: right ? 254 : 0, paddingLeft: left ? 254 : 0, position: "relative" }}>
          <div ref={this.wrapperRef} className="aiv-canvas">
            <img
              ref={this.imageRef}
              src={imageSrc}
              alt="Annotated"
              className="aiv-img"
              onLoad={this.updateSize}
              draggable={false}
            />
            <svg className="aiv-svg" style={{ overflow: "visible" }} viewBox={\`0 0 \${w} \${h}\`} preserveAspectRatio="none">
              {annotations.map(ann => {
                const p = this.linePoints(ann);
                if (!p) return null;
                return <line key={ann.id} x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2}
                  stroke={activeId === ann.id ? "#1a1a1a" : "#c0bbaf"}
                  strokeWidth={activeId === ann.id ? 2 : 1.5} strokeDasharray="5,3" />;
              })}
              {annotations.map((ann, i) => {
                const cx = (ann.pointX / 100) * w;
                const cy = (ann.pointY / 100) * h;
                const isActive = activeId === ann.id;
                return (
                  <g key={ann.id} className="aiv-dot"
                    onClick={e => { e.stopPropagation(); this.setState(s => ({ activeId: s.activeId === ann.id ? null : ann.id })); }}>
                    <circle cx={cx} cy={cy} r={13} fill="none" stroke="#1a1a1a"
                      strokeWidth={isActive ? 2.5 : 0} opacity={0.15}
                      style={{ transition: "stroke-width 0.15s" }} />
                    <circle cx={cx} cy={cy} r={8} fill={isActive ? "#1a1a1a" : "#fff"} stroke="#1a1a1a" strokeWidth={2}
                      style={{ transition: "fill 0.15s" }} />
                    <text x={cx} y={cy + 4} textAnchor="middle" fontSize={10} fontWeight={600}
                      fontFamily="DM Sans, sans-serif" fill={isActive ? "#fff" : "#1a1a1a"}
                      style={{ pointerEvents: "none", userSelect: "none", transition: "fill 0.15s" }}>{i + 1}</text>
                  </g>
                );
              })}
            </svg>
            <div className="aiv-layer">
              {annotations.map((ann, i) => (
                <div key={ann.id} className={\`aiv-card\${activeId === ann.id ? " active" : ""}\`}
                  style={this.cardStyle(ann)}
                  onClick={e => { e.stopPropagation(); this.setState(s => ({ activeId: s.activeId === ann.id ? null : ann.id })); }}>
                  <div className="aiv-num">{i + 1}</div>
                  <h4>{ann.label}</h4>
                  <p>{ann.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default AnnotatedImageViewer;
`;
}

// ─────────────────────────────────────────────
// MAIN: AnnotatedImageStudio
// ─────────────────────────────────────────────
class AnnotatedImageStudio extends Component {
  constructor(props) {
    super(props);
    this.state = {
      imageSrc: null,
      annotations: [],
      mode: "view", // 'view' | 'placing'
      activeId: null,
      editingAnnotation: null,
      pendingPoint: null,
      dragOverUpload: false,
      generatedCode: null,
      copied: false,
      viewTab: "editor", // 'editor' | 'preview'
    };
    this.fileInputRef = createRef();
  }

  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) =>
      this.setState({
        imageSrc: ev.target.result,
        annotations: [],
        generatedCode: null,
      });
    reader.readAsDataURL(file);
  };

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ dragOverUpload: false });
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) =>
      this.setState({
        imageSrc: ev.target.result,
        annotations: [],
        generatedCode: null,
      });
    reader.readAsDataURL(file);
  };

  handleImageClick = (e) => {
    if (this.state.mode !== "placing") return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    this.setState({
      pendingPoint: { x, y },
      editingAnnotation: {
        id: Date.now(),
        label: "",
        description: "",
        pointX: x,
        pointY: y,
        side: x > 50 ? "right" : "left",
      },
    });
  };

  saveAnnotation = () => {
    const { editingAnnotation, annotations } = this.state;
    if (!editingAnnotation?.label?.trim()) return;
    const exists = annotations.find((a) => a.id === editingAnnotation.id);
    const updated = exists
      ? annotations.map((a) =>
          a.id === editingAnnotation.id ? editingAnnotation : a,
        )
      : [...annotations, editingAnnotation];
    this.setState({
      annotations: updated,
      editingAnnotation: null,
      pendingPoint: null,
      mode: "view",
      generatedCode: null,
    });
  };

  deleteAnnotation = (id) => {
    this.setState((s) => ({
      annotations: s.annotations.filter((a) => a.id !== id),
      generatedCode: null,
    }));
  };

  editAnnotation = (ann) => {
    this.setState({
      editingAnnotation: { ...ann },
      pendingPoint: { x: ann.pointX, y: ann.pointY },
      mode: "placing",
    });
  };

  cancelEdit = () => {
    this.setState({
      editingAnnotation: null,
      pendingPoint: null,
      mode: "view",
    });
  };

  generateCode = () => {
    const { imageSrc, annotations } = this.state;
    const code = generateViewerCode(imageSrc, annotations);
    this.setState({ generatedCode: code });
  };

  copyCode = () => {
    navigator.clipboard.writeText(this.state.generatedCode).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  render() {
    const {
      imageSrc,
      annotations,
      mode,
      editingAnnotation,
      pendingPoint,
      dragOverUpload,
      generatedCode,
      copied,
      viewTab,
    } = this.state;

    const hasAnnotations = annotations.length > 0;

    return (
      <div className="ai-root">
        <style>{sharedStyles}</style>

        {/* Tab switcher (only when image is loaded) */}
        {imageSrc && (
          <div
            style={{
              display: "flex",
              gap: 6,
              marginBottom: 20,
              borderBottom: "1.5px solid #e8e3dc",
              paddingBottom: 0,
            }}
          >
            {["editor", "preview"].map((tab) => (
              <button
                key={tab}
                onClick={() => this.setState({ viewTab: tab })}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "8px 18px",
                  border: "none",
                  borderBottom:
                    viewTab === tab
                      ? "2.5px solid #1a1a1a"
                      : "2.5px solid transparent",
                  background: "transparent",
                  color: viewTab === tab ? "#1a1a1a" : "#aaa",
                  cursor: "pointer",
                  borderRadius: "0",
                  marginBottom: "-1.5px",
                  transition: "color 0.15s",
                  textTransform: "capitalize",
                }}
              >
                {tab === "editor" ? "✏️ Editor" : "👁 Preview"}
              </button>
            ))}
          </div>
        )}

        {/* ── EDITOR TAB ── */}
        {(!imageSrc || viewTab === "editor") && (
          <>
            {/* Upload */}
            <div
              className={`ai-upload-zone${dragOverUpload ? " drag-over" : ""}`}
              onClick={() => this.fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                this.setState({ dragOverUpload: true });
              }}
              onDragLeave={() => this.setState({ dragOverUpload: false })}
              onDrop={this.handleDrop}
            >
              <div className="ai-upload-label">
                {imageSrc ? "Replace Image" : "Upload an Image"}
              </div>
              <div className="ai-upload-sub">Click or drag & drop</div>
              <input
                ref={this.fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={this.handleFileChange}
              />
            </div>

            {imageSrc && (
              <>
                {/* Controls */}
                <div className="ai-controls">
                  <button
                    className={`ai-btn${mode === "placing" ? " primary" : ""}`}
                    onClick={() =>
                      this.setState((s) =>
                        s.mode === "placing"
                          ? {
                              mode: "view",
                              editingAnnotation: null,
                              pendingPoint: null,
                            }
                          : { mode: "placing" },
                      )
                    }
                  >
                    {mode === "placing"
                      ? "✕ Cancel Placement"
                      : "+ Add Annotation"}
                  </button>
                  {hasAnnotations && (
                    <span style={{ fontSize: 13, color: "#aaa" }}>
                      {annotations.length} annotation
                      {annotations.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                {mode === "placing" && !pendingPoint && (
                  <p className="ai-instructions">
                    Click anywhere on the image to place your annotation point.
                  </p>
                )}

                <AnnotationDisplay
                  imageSrc={imageSrc}
                  annotations={annotations}
                  editable={true}
                  mode={mode}
                  pendingPoint={pendingPoint}
                  onImageClick={this.handleImageClick}
                  onEdit={this.editAnnotation}
                  onDelete={this.deleteAnnotation}
                />

                {/* Inline editor */}
                {editingAnnotation && (
                  <div className="ai-editor">
                    <h3>
                      {annotations.find((a) => a.id === editingAnnotation.id)
                        ? "Edit"
                        : "New"}{" "}
                      Annotation
                    </h3>
                    {!pendingPoint && (
                      <p
                        className="ai-instructions"
                        style={{ marginBottom: 12 }}
                      >
                        Click on the image above to set the point location.
                      </p>
                    )}
                    {pendingPoint && (
                      <>
                        <div className="ai-editor-row">
                          <div
                            className="ai-field"
                            style={{ flex: 1, minWidth: 180 }}
                          >
                            <label>Label</label>
                            <input
                              type="text"
                              value={editingAnnotation.label}
                              placeholder="Short title..."
                              onChange={(e) =>
                                this.setState((s) => ({
                                  editingAnnotation: {
                                    ...s.editingAnnotation,
                                    label: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                          <div className="ai-field">
                            <label>Side</label>
                            <select
                              value={editingAnnotation.side}
                              onChange={(e) =>
                                this.setState((s) => ({
                                  editingAnnotation: {
                                    ...s.editingAnnotation,
                                    side: e.target.value,
                                  },
                                }))
                              }
                            >
                              <option value="right">Right</option>
                              <option value="left">Left</option>
                            </select>
                          </div>
                        </div>
                        <div className="ai-field" style={{ marginBottom: 14 }}>
                          <label>Description</label>
                          <textarea
                            value={editingAnnotation.description}
                            placeholder="Longer explanation..."
                            onChange={(e) =>
                              this.setState((s) => ({
                                editingAnnotation: {
                                  ...s.editingAnnotation,
                                  description: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                          <button
                            className="ai-btn primary"
                            onClick={this.saveAnnotation}
                          >
                            Save Annotation
                          </button>
                          <button className="ai-btn" onClick={this.cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Generate section */}
                {hasAnnotations && !editingAnnotation && (
                  <div className="ai-generate-section">
                    <h3>Generate Frontend Component</h3>
                    <p>
                      When you're done placing annotations, generate a
                      self-contained{" "}
                      <code
                        style={{
                          background: "#e8e3dc",
                          padding: "2px 5px",
                          borderRadius: 4,
                          fontSize: 12,
                        }}
                      >
                        AnnotatedImageViewer.jsx
                      </code>{" "}
                      you can drop straight into your website — no editing
                      needed.
                    </p>
                    <button
                      className="ai-btn generate"
                      onClick={this.generateCode}
                    >
                      <span>⚡</span> Generate Component
                    </button>

                    {generatedCode && (
                      <div className="ai-codebox" style={{ marginTop: 20 }}>
                        <div className="ai-codebox-header">
                          <div>
                            AnnotatedImageViewer.jsx
                            <span style={{ marginLeft: 10 }}>
                              — copy & paste into your project
                            </span>
                          </div>
                          <button
                            className="ai-copy-btn"
                            onClick={this.copyCode}
                          >
                            {copied ? "✓ Copied!" : "Copy"}
                          </button>
                        </div>
                        <pre>{generatedCode}</pre>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {!imageSrc && (
              <div
                style={{
                  textAlign: "center",
                  color: "#bbb",
                  fontSize: 14,
                  marginTop: 8,
                }}
              >
                Upload an image to start annotating.
              </div>
            )}
          </>
        )}

        {/* ── PREVIEW TAB ── */}
        {imageSrc && viewTab === "preview" && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 16,
                gap: 10,
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: "#888" }}>
                This is exactly how your component will look on your website.
              </p>
              <span className="ai-viewer-badge">
                <span>●</span> Read-only
              </span>
            </div>
            {annotations.length === 0 && (
              <p style={{ fontSize: 13, color: "#bbb", fontStyle: "italic" }}>
                No annotations yet — add some in the Editor tab.
              </p>
            )}
            <AnnotationDisplay
              imageSrc={imageSrc}
              annotations={annotations}
              editable={false}
              mode="view"
              pendingPoint={null}
              onImageClick={() => {}}
              onEdit={() => {}}
              onDelete={() => {}}
            />
          </div>
        )}
      </div>
    );
  }
}

export default AnnotatedImageStudio;
